<script lang="ts">
import { defineComponent } from 'vue';
import { checkShouldDisplaySameUrl, extractAndPurifyHtml } from '../services/utilService';
import { mapActions, mapGetters } from 'vuex';

export default defineComponent({
  name: 'Eula',
  data () {
    return {
      policyDesign: {
        width: '100%' as string
      },
      safeBodyHtml: '',
      styleEl: null as HTMLStyleElement | null,
      isLiveEulaPpMode: true
    };
  },
  props: {
    isUq: Boolean
  },
  computed: {
    ...mapGetters([
      'appVersion',
      'eulaVersion'
    ]),
    checkShouldDisplaySameUrl (): boolean {
      return checkShouldDisplaySameUrl();
    }
  },
  methods: {
    ...mapActions([
      'updateLegalSettings',
      'getEulaPpHtml'
    ]),
    sendHeightToParent () {
      const height = window.document.getElementsByClassName('eula')[0]?.clientHeight;
      window.parent.postMessage({
        solutoDynamicHtmlInfo: {
          height: height ? height + 'px' : undefined
        }
      }, '*');
    },
    populateHtmlContent (contentHtml: string) {
      const { purifiedHtml, styleEl } = extractAndPurifyHtml(contentHtml);
      this.styleEl = styleEl;
      this.safeBodyHtml = purifiedHtml;
      document.head.appendChild(this.styleEl);
      this.$nextTick(() => {
        this.sendHeightToParent();
      });
    }
  },
  async created () {
    document.title = '使い方サポート利用規約';
    this.policyDesign.width = window.document.getElementById('app')?.offsetWidth + 'px';
    const brand = this.isUq ? 'UQ' : 'AU';
    this.isLiveEulaPpMode = await this.updateLegalSettings(brand);
    if (this.isLiveEulaPpMode) {
      const contentHtml = await this.getEulaPpHtml({ htmlContentType: 'EULA', brand, version: this.eulaVersion });
      this.populateHtmlContent(contentHtml.htmlBody);
    }
  },
  async mounted () {
    this.sendHeightToParent();
  },
  unmounted () {
    if (this.styleEl) {
      document.head.removeChild(this.styleEl);
    }
  }
});
</script>
<template>
  <div class="eula" id="eula" :style="policyDesign">
    <div v-if="isLiveEulaPpMode" id="legal-content" v-html="safeBodyHtml"></div>
    <!-- Default EULA which is currently set staticly in the app (This will not needed annymore, it was when there was no admin UI html ready back then) -->
    <div v-else>
        <H1>使い方サポート利用規約</H1>
        <p class="subtitle">第1条（適用）</p>
        <ol>
            <li>KDDI株式会社および沖縄セルラー電話株式会社 (以下「当社」といいます)は、使い方サポート利用規約 (以下「本利用規約」といいます) に従って「使い方サポート」 （以下「本サービス」といいます)を提供します。本サービスのご利用にあたっては、本利用規約にご同意の上、これに従って頂く必要があります。本サービスの利用に際しては、本利用規約の他、当社の定める各種の規約、注意事項、ガイドライン等（以下、併せて「個別規約」といいます）が適用されます。個別規約は名称の如何に拘らず本利用規約の一部を構成するものとし、本利用規約の規定と、個別規約の規定とが異なる場合には、個別規約の規定が優先して適用されるものとします。</li>
            <li>当社は、民法の定めに従い、本利用規約および個別規約を変更することができます。この場合、本サービスの提供条件は変更後の本利用規約および個別規約によります。なお、当社は、変更後の本利用規約および個別規約ならびに当該変更の効力発生時期を、所定のWebサイトおよびその他相当の方法で周知するものとし、変更後の本利用規約および個別規約は、当該効力発生時期が到来した時点で効力を生じるものとします。</li>
            <li>当社は、本サービスの提供または運営に関する業務の一部または全てを、業務委託先に委託することができます。</li>
        </ol>
        <p class="subtitle">第2条（用語の定義等）</p>
        <p>本利用規約において用いられる次の用語は、それぞれ次の意味で使用します。</p>
        <table border="1" style="border-collapse: collapse" cellpadding="5">
            <tr>
                <th>用語</th>
                <th>用語の意味</th>
            </tr>
            <tr>
                <td>約款</td>
                <td>au(LTE)通信サービス契約約款、au(5G)通信サービス契約約款およびUQ mobile通信サービスⅡ契約約款</td>
            </tr>
            <tr>
                <td>通信サービス</td>
                <td>au(LTE)通信サービス、 au(5G)通信サービスおよびUQ mobile通信サービスⅡ</td>
            </tr>
            <tr>
                <td>au通信サービス</td>
                <td>au(LTE)通信サービスおよびau(5G)通信サービス</td>
            </tr>
            <tr>
                <td>通信サービス契約</td>
                <td>当社から通信サービスの提供を受けるための契約</td>
            </tr>
            <tr>
                <td>通信サービス契約者</td>
                <td>当社と通信サービス契約を締結している者</td>
            </tr>
            <tr>
                <td>通信サービス利用権</td>
                <td>通信サービス契約に基づき、当社から通信サービスの提供を受ける権利</td>
            </tr>
            <tr>
                <td>本契約</td>
                <td>当社から本サービスの提供を受けるための契約</td>
            </tr>
            <tr>
                <td>本アプリ</td>
                <td>当社所定の端末にインストールして利用する、本サービスの専用アプリケーション</td>
            </tr>
            <tr>
                <td>サポート会員</td>
                <td>当社から本サービスを受けるお客様であり、有料サポート会員と無料サポート会員を併せた総称</td>
            </tr>
            <tr>
                <td>有料サポート会員</td>
                <td>サポート会員のうち、第4条に定める月額料を支払うことによって本サービスの全てが利用できるお客様</td>
            </tr>
            <tr>
                <td>無料サポート会員</td>
                <td>サポート会員のうち、有料サポート会員以外のお客様</td>
            </tr>
        </table>
        <p class="subtitle">第3条（ご利用に係る契約）</p>
        <ol>
            <li>本サービスへの入会を希望されるお客様は、本利用規約に同意の上、本サービスへの入会のお申込み手続きをしていただく必要があります。お客様が入会のお申込み手続きを行った時点で、本利用規約を本契約の内容とすることに合意されたものと見なします。</li>
            <li>本サービスへの入会を申し込まれるお客様（以下「申込者」といいます）は、当社が定める手続きにより、本サービスの入会の申込みを行い、当社が承諾したときをもってサポート会員としての資格を取得します。</li>
            <li>当社は、前二項の規定にかかわらず、次の各号のいずれかに該当すると判断した場合、申込みを承諾しないことがあります。
                <ol class="paren">
                    <li>申込者が、当社が提供するサービス等の料金の支払いを現に怠り、または怠るおそれがあるとき。</li>
                    <li>申込者が、申込みの際に虚偽の事項を申告したとき。</li>
                    <li>当社の業務遂行上著しく支障があるとき。</li>
                </ol>
            </li>
            <li>当社は、1の通信サービス契約につき、1の本契約を締結します。</li>
            <li>申込者が、次の各号のいずれかに該当する場合は本契約の締結はできません。
                <ol class="paren">
                    <li>申込者の本契約に係る通信サービス契約が次の端末機器（※）に関して締結されたものであるとき。
                        <BR/>※ ぷりペイド専用電話、データ通信機器、デジタル通信ライフ機器等、当社が別途定める端末（以下「対象端末」といいます）以外の端末。
                    </li>
                    <li>本契約に係る通信サービスの契約回線について、当社が別途提供する「auスマートサポート」サービスの利用にかかる契約が締結されているとき。</li>
                </ol>
            </li>
        </ol>
        <p class="subtitle">第4条（月額料）</p>
        <ol>
            <li>有料サポート会員の本サービスの月額ご利用料金(以下「月額料」といいます)は、649円（税込）とします。</li>
            <li>前項の定めにかかわらず、本契約に係る通信サービスの契約回線のお客様コードにおいて、以下いずれかのサービスを契約している場合（以下、この条件を「安心サポートパック適用条件1」といいます）の月額料は319円（税込）とします。
                <ol class="paren">
                    <li>故障紛失サポート</li>
                    <li>故障紛失サポート with Cloud</li>
                    <li>故障紛失サポート ワイド with Cloud
                    <BR/>なお、月の途中で安心サポートパック適用条件1を満たした場合または月の途中で安心サポートパック適用条件1を満たさなくなった場合の月額料は、以下の計算式によって、計算を行います。
                    </li>
                </ol>
                <div class="calculation">
                    ※計算式
                    <BR/>649円（税込）×本サービスに加入していた日／当該月の日数
                    <BR/>－330円（税込）×安心サポートパック適用条件1を満たした日／当該月の日数
                    <BR/>
                    <BR/>消費税計算上、請求の際に合計額が異なる場合があります。
                </div>
            </li>
            <li>前二項の定めにかかわらず、本契約に係る通信サービスの契約回線のお客様コードにおいて、以下いずれかのサービスを契約している場合（以下、この条件を「安心サポートパック適用条件2」といいます）、安心サポートパック適用条件2を満たしている期間（安心サポートパック適用条件2を1日でも満たした月は含みます）の月額料は231円（税込）とします。
                <ol class="paren">
                    <li>故障紛失サポート with AppleCare Services</li>
                    <li>故障紛失サポート with AppleCare Services & iCloudストレージ</li>
                    <li>故障紛失サポート with AppleCare Services & iCloud＋</li>
                    <li>故障紛失サポート ワイド with AppleCare Services & iCloud+
                        <BR/>なお、安心サポートパック適用条件1と安心サポートパック適用条件2の双方を満たす場合は、本項の定めが優先して適用されるものとし、本項の条件を満たさなくなった場合でも、前項の条件を満たす場合は、前項の定めが適用されるものとします。
                    </li>
                </ol>
            </li>
            <li>月の途中で本サービスに有料サポート会員となった場合または有料サポート会員ではなくなった場合、本条第1項乃至第3項に定める月額料は、ご利用日数分の日割額となります。</li>
            <li>月額料の請求、督促、その他債権・債務の管理等は、全て通信サービスの付加機能の月額使用料と同様に取り扱います。</li>
        </ol>
        <p class="subtitle">第5条（本サービスの内容）</p>
        <ol>
            <li>当社は、サポート会員に対し、本サービスとして、以下の各号に定めるサービスを提供します。有料サポート会員は本サービスの全てを利用できます。それぞれの提供内容、条件については、次の各号に定める条項に定めます。ただし、対象端末以外の端末では本サービスをご利用いただけません。</li>
            <ol class="paren">
                <li>電話サポート　第6条</li>
                <li>遠隔操作サポート　第7条</li>
                <li>本アプリを通じて提供するサービス（以下「本アプリ上サービス」といいます）　第8条</li>
            </ol>
            <li>無料サポート会員は、第8条第1項第1号、第5号及び第6号に定めるサービスのみご利用頂けます。</li>
        </ol>
        <p class="subtitle">第6条（電話サポート）</p>
        <ol>
            <li>電話サポートの内容<BR/>サポート会員から当社指定の専用デスク(以下「本デスク」といいます)に電話をして頂くことで、サポート会員に次の各号に定める事項を電話でご案内します。
                <ol class="paren">
                    <li>対象端末の初期設定および基本操作の案内</li>
                    <li>au ID、my UQ mobile ID、Apple IDおよびGoogleアカウントの設定方法の案内</li>
                    <li>対象端末にプリインストールされているアプリケーションの設定方法および一般的な個人利用の範囲内の利用方法の案内</li>
                    <li>当社が提供しているコンテンツおよび推奨アプリケーションの設定方法および利用方法の案内</li>
                    <li>対象端末と当社提供機器との接続方法、設定方法および利用方法の案内</li>
                    <li>当社提供の各サービスの概要および同サービスの利用方法の案内</li>
                    <li>GooglePlayおよびApp Storeの基本的な操作方法の案内</li>
                    <li>当社が別途定める、当社以外の第三者が提供する各サービスおよび各アプリケーションの設定方法および一般的な個人利用の範囲内の利用方法の案内</li>
                    <li>対象端末と当社以外の第三者が提供する当社が別途定める機器との接続方法、設定方法および利用方法の案内</li>
                </ol>
            </li>
            <BR/>
            <li>利用条件
                <ol class="paren">
                    <li>電話サポートの内容の詳細については、当社が別に定めるところによります。</li>
                    <li>また、前項に規定する電話サポートの内容に該当する場合であっても、サポート会員の機器類が故障等により正常に動作しないとき等には、対応できない場合があります。</li>
                    <li>電話サポートによるご案内の内容は、当社、各サービスおよび各アプリケーションの提供事業者ならびに周辺機器のメーカーが公表している操作および設定に関するものに限られるものとし、一部ご案内の対象外となる操作および設定があります。</li>
                </ol>
            </li>
            <BR/>
            <li>利用時間<BR/>電話サポートの利用可能時間は、当社が別途定めるものとします。</li>
        </ol>
        <p class="subtitle">第7条（遠隔操作サポート）</p>
        <ol>
            <li>遠隔操作サポートの内容
                <BR/>サポート会員は、株式会社オプティムが提供するアプリケーション(以下「遠隔操作サポートアプリ」といいます。)を利用し、本デスクのオペレーターを通じて、当社が別途指定するスマートフォン端末、タブレット端末及びパソコン等 (以下「対応端末」といいます)の遠隔操作サポート(サポート会員からの対応端末の設定、操作等に関するお問い合わせに対して、本デスクのオペレーターによる音声通話、及び、対応端末の遠隔操作による案内をするサービス) を利用することができます。</li>
            <BR/>
            <li>利用条件
                <ol class="paren">
                    <li>遠隔操作サポートの提供を受ける場合、サポート会員は、株式会社オプティムが定める「使用許諾契約書」等および遠隔操作サポートアプリの利用に係る規約に同意する必要があります。また、サポート会員は、遠隔操作サポートアプリをご自身でインストールし、随時最新のバージョンにアップデートするものとします。遠隔操作サポートアプリのインストールおよびアップデートに必要な通信料等は、サポート会員のご負担となります。</li>
                    <li>サポート会員が遠隔操作サポートの提供を受けるには、本デスクのオペレータによって対応端末内の情報が遠隔で閲覧されることについて承諾して頂く必要があります。また、遠隔操作サポートを希望される内容によっては、本デスクのオペレータの判断により、遠隔操作サポートを利用せず、音声通話のみでサポートを行う場合があります。</li>
                    <li>遠隔操作サポートが提供するご案内は、当社、各サービスおよび各アプリケーションの提供事業者ならびに周辺機器のメーカーが公表している操作および設定に関するものに限られるものとし、一部ご案内の対象外となる操作および設定があります。</li>
                </ol>
            </li>
            <BR/>
            <li>利用時間<BR/>第6条第3項に定める電話サポートの利用可能時間と同一とします。</li>
        </ol>
        <p class="subtitle">第8条（本アプリ上サービス）</p>
        <ol>
            <li>本アプリ上サービスの内容
                <BR/>当社は、本アプリ上サービスとして、以下の各号に定めるサービスを提供します。
                <ol class="paren">
                    <li>対象端末のトラブル発生時に、サポート会員が自ら解決できるようにサポートするサービス。</li>
                    <li>当社からの各種サービスに関するご案内やその他の通知を配信するサービス。</li><li>電話サポートおよび遠隔操作サポートを提供する本デスクへ問合せができるサービスおよび問合せの予約ができるサービス。</li>
                    <li>サポート会員よりご相談内容のメッセージを当社に所定の方法によりお送り頂くことで、当社から所定のメッセージにてご案内を行うサービス。</li>
                    <li>本アプリがインストールされた端末のバッテリー残量、OS、アプリおよび設定状況、Wi-Fiの接続スピード状況、端末が接続されているWi-Fi上のデバイス情報、IPアドレス等、当社が本サービスを提供するために必要な情報等を定期的に当社へ自動で送信するサービス。</li>
                    <li>本アプリがインストールされた端末の状態や動作不具合等を診断するサービス。</li>
                </ol>
            </li>
            <BR/>
            <li>利用条件</li>
                <ol class="paren">
                    <li>本アプリ上サービスを利用するには、対象端末において本アプリをダウンロードいただくとともに、当社が定める「使い方サポートアプリに関するアプリケーション・プライバシーポリシー(以下「本アプリプラポリ」といいます。)」に同意いただく必要があります。</li>
                    <li>本アプリを利用するためには、当社が提供するau IDを対象端末に設定のうえ、当該au IDによる本アプリへのログインおよび当社所定の手続きを行っていただく必要があります。au IDの設定、登録、利用に関しては、当社が定めるID利用規約が適用されます。
                        <BR/>※ID利用規約：
                        <BR/>https://media3.kddi.com/extlib/files/corporate/kddi/kokai/keiyaku_yakkan/pdf/auid.pdf
                    </li>
                    <li>サポート会員がご利用の対象端末の種類および当該対象端末におけるお客様情報の利用設定等によって、本アプリ上サービスにおいて利用できる機能等が異なる場合があります。</li>
                    <li>本アプリは、本アプリがインストールされた端末に関する情報を収集します。サポート会員は、本アプリにより収集または保存される情報について、当社が本利用規約第12条の定めに従い取り扱うことに同意するものとします。</li>
                    <li>本アプリが収集する前項第5号および前号に記載の情報は、サポート会員に対して、本アプリがインストールされた端末がどのように機能しているか、また、本アプリが当該端末の様々な機能を向上させるためにサポート会員にどのような対応を取って頂けるかを通知する目的および本アプリプラポリに定める目的に用いられます。本アプリが当該端末から収集する情報は、これらが収集された目的を達成するため、当社が定める本アプリプラポリに則り、保存または保管されます。</li>
                    <li>前項に規定する各機能の詳細については、当社が別に定めるところによります。</li>
                    <li>前項第4号に定めるメッセージによるご案内の内容は、当社、各サービスおよび各アプリケーションの提供事業者ならびに周辺機器のメーカーが公表している操作および設定に関するものに限られるものとし、一部ご案内の対象外となる操作および設定があります。</li>
                    <li>サポート会員が本サービスを解約した場合、前項第5号に定める情報等の当社への送信は解約後所定の期間内に停止されます。サポート会員が本サービスを解約せず、かつ当社への情報の送信を望まない場合、サポート会員において、本アプリを端末から完全かつ回復不能な形で消去する必要があります。</li>
                </ol>
            <BR/>
            <li>利用時間<BR/>第1項第4号に定めるメッセージによるご案内のご利用可能時間は、当社が別途定めるものとします。</li>
            <BR/>
            <li>本アプリのアップデート</li>
                <ol class="paren">
                    <li>サポート会員は、本アプリを随時最新のバージョンにアップデートするものとします。</li>
                    <li>当社は、サポート会員に通知することなく本アプリのアップデートを行うことができるものとします。</li>
                    <li>本アプリのインストールおよびアップデートに必要な通信料等は、サポート会員のご負担となります。</li>
                </ol>
        </ol>
        <p class="subtitle">第9条（権利の譲渡）</p>
        <p>サポート会員は、本サービスおよび本アプリに係るいかなる権利および義務も第三者に移転、譲渡、担保設定またはその他の処分をすることはできません。ただし、本契約に係る通信サービス利用権の譲渡または承継時に限り、本サービスおよび本アプリの利用権の譲渡または承継の請求をすることができ、当社は、当該譲渡または承継を承諾するものとします。</p>
        <BR/>
        <p class="subtitle">第10条（サポート会員の退会、本契約の終了）</p>
        <ol>
            <li>サポート会員は、本サービスからの退会を希望されるときは、当社が別に定める方法に従い当社に対して本サービスからの退会を申し出るものとします。当該申し出を当社が受領した日をもって、サポート会員は本サービスから退会し、本契約が終了するものとします。</li>
            <li>サポート会員が次の各号のいずれかに該当する場合、当社は、何ら通知、催告なく本サービスより退会させて本契約を終了することができます。
                <ol class="paren">
                    <li>入会時に虚偽の内容を申告した場合</li>
                    <li>本利用規約に違反した場合</li>
                    <li>月額料およびその他の本サービスに係る料金、ならびに通信サービス料金その他の当社の電気通信サービス等に係る料金の支払を、支払期限までに行わなかった場合</li>
                    <li>そのほか、当社がサポート会員として不適格であると判断した場合</li>
                </ol>
            </li>
            <li>通信サービス契約が解除等により終了した場合、本契約も終了するものとします。ただし、次の各号のいずれかの端末の買い替えを伴わない番号移行により通信サービス契約が解除された場合であるときは、本契約は終了しないものとします。
                <ol class="paren">
                    <li>au通信サービスの利用に係る契約から、UQ mobile通信サービスⅡの利用に係る契約への番号移行。</li>
                    <li>UQ mobile通信サービスⅡの利用に係る契約から、au通信サービスの利用に係る契約への番号移行。</li>
                </ol>
            </li>
            <li>前項ただし書の定めにかかわらず、前項各号の番号移行をau Online ShopまたはUQ mobileオンラインショップで行う場合、本契約の終了又は継続をご選択いただくこととなります。</li>
            <li>本契約が終了にともない、サポート会員が有する本サービスに関するすべての権利は、失効するものとします。</li>
            <li>当社は、事由の如何を問わず本契約が終了した場合、サポート会員の本サービスおよび本アプリのご利用に係る情報等をすべて消去することができるものとします。</li>
        </ol>
        <p class="subtitle">第11条（知的財産権）</p>
        <p>本サービスおよび本アプリに関する著作権等を含む一切の知的財産権その他の権利は、当社または当社に対して使用許諾を行っている第三者に帰属します。</p>
        <p class="subtitle">第12条（個人情報の取り扱い）</p>
        <p>本サービスおよび本アプリの提供にあたり取得した個人情報の取り扱いについては、各社の「プライバシーポリシー」および本アプリプラポリが適用されます。</p>
            <BR/>KDDIプライバシーポリシー：
            <BR/>https://www.kddi.com/corporate/kddi/public/privacy/
            <BR/>沖縄セルラープライバシーポリシー：
            <BR/>https://okinawa-cellular.jp/corporate/disclosure/privacypolicy/
        <p class="subtitle">第13条（禁止事項）</p>
        <ol>
            <li>サポート会員は、本サービス（本サービスの利用に関して当社からサポート会員に提供した情報を含みます）および本アプリをサポート会員個人のご利用のためのみに利用することができるものとし、商業・広告目的その他当社が許可していない目的での利用および二次利用を一切してはならないものとします。</li>
            <li>サポート会員は、本サービスおよび本アプリに関して次の各号に定める行為を一切行ってはならないものとします。
                <ol class="paren">
                    <li>当社または第三者の権利を侵害するまたはその虞がある行為</li>
                    <li>本サービスおよび本アプリの運営を妨害するまたはその虞がある行為（過度に問合せを繰り返す、本サービスの範囲を超える過度な問合せまたは要求を行う等の行為を含みます）</li>
                    <li>法令に違反するまたは違反する可能性がある行為</li>
                    <li>当社が定めた方法以外の方法で本サービスおよび本アプリの一部もしくは全部を利用する行為、またはそのおそれがある行為</li>
                    <li>その他合理的な理由に基づき不適当と判断される行為</li>
                </ol>
            </li>
            <li>サポート会員は、本アプリおよび本アプリを通じて得るデータおよび情報等につき、当社の事前の承諾を得ることなく無断で、転載・複製・複写・修正・改変・公衆送信・頒布・貸与・翻訳・翻案・二次的著作物の作成、リバースエンジニアリング、逆コンパイル、逆アセンブル等を行うことはできず、また、第三者に譲渡、使用許諾、その他の処分を行うことはできません。</li>
        </ol>
        <p class="subtitle">第14条（免責）</p>
        <ol>
            <li>サポート会員は、本利用規約の内容を理解した上で、本サービスおよび本アプリをサポート会員自身の責任で利用するものとします。</li>
            <li>当社は、サポート会員からの問合せを遅滞無く受け付けることを保証するものではありません。</li>
            <li>当社は、サポート会員の問題・課題等を特定できること、または問題・課題等を解決できることを保証するものではありません。</li>
            <li>当社は、本デスクにおいて、サポート会員の問題・課題等の全てに対応できるものではありません。問合せの内容によっては、当社の別の窓口をご案内させて頂き、当該窓口へ問合せを行って頂く場合があります。</li>
            <li>本サービスは、当社は、対象端末の販売事業者もしくは製造業者、ソフトウェアの販売事業者、アプリケーションの提供事業者、または対象端末に関するサービスの提供事業者（以下併せて「メーカー等」といいます。）が提供する正規サポートを代行するサービスではありません。問合せの内容によっては、問合せの対象となる対象端末、ソフトウェア、アプリケーション、サービスを提供するメーカー等のホームページの紹介や、利用者自身による当該メーカー等への問合せ依頼に留まる場合があります。</li>
            <li>本デスクの電話番号は、当社が別途指定します。</li>
            <li>当社は、本アプリの動作内容および結果について、その完全性、正確性、安全性、目的適合性または有用性等につき、何ら保証するものではなく、当該内容および結果が第三者の権利を侵害しないこと等について何ら保証するものではありません。</li>
            <li>サポート会員は、対象端末で本アプリの各機能を利用することができますが、当社は、本アプリが各サポート会員のご使用の端末と互換性があることを保証するものではありません。</li>
            <li>データのバックアップは、定期的にサポート会員自身の責任で実施いただくこととします。</li>
            <li>前号に定めるサポート会員によるデータのバックアップの実施にかかわらず、当社は、サポート会員が本サービスを利用したことにより生じた不利益（データ (アドレス帳、データフォルダ、メール等) の変化、消失等を含むがこれに限らない）について、当社の責めに帰すべき事由がある場合を除き、一切の責任を負わないものとします。</li>
            <li>本サービスおよび本アプリのご利用の際に生じる接続料および通信料等は、サポート会員が負担するものとします。</li>
            <li>本アプリの利用に必要な対象端末、コンピューター、ソフトウェアその他の機器、通信回線その他の通信環境等の準備および維持 (ウィルス感染の防止対策等、セキュリティ対策を含みます) は、サポート会員の費用と責任において行うものとします。</li>
        </ol>
        <p class="subtitle">第15条（本サービスおよび本アプリの変更、廃止等）</p>
        <ol>
            <li>当社は、サポート会員へ事前の通知または周知を行うことにより、本サービスおよび本アプリの全部または一部を変更または廃止することができるものとします。</li>
            <li>当社は、サポート会員に事前に通知することなく、保守作業、システム復旧、停電・天災地変・伝染病の蔓延等の不可抗力、協業先のサービス終了またはその他のやむをえない理由により本サービスおよび本アプリの提供を中止または中断することがあります。</li>
        </ol>
        <p class="subtitle">第16条（本サービスおよび本アプリの終了等）</p>
        <p>本サービスおよび本アプリは当社の都合により、サポート会員へ事前に周知のうえ、その一部またはすべてを停止または終了できるものとします。</p>
        <p class="subtitle">第17条（損害賠償）</p>
        <p>本サービスおよび本アプリの提供にあたり、当社の責に帰すべき事由によりサポート会員が損害を被った場合、当社は、本サービスの月額料の1ヶ月分を上限として当社はその損害賠償責任を負うものとします。ただし、当社の故意または重大な過失に基づく損害については、この限りではありません。</p>
        <p class="subtitle">第18条（裁判管轄）</p>
        <p>本利用規約の解釈や本サービスおよび本アプリの提供条件等について疑義が生じ、または本利用規約に定めがない事項が生じた場合は、当社とサポート会員は誠意をもって協議することとしますが、それでもなお解決しない場合には東京地方裁判所を第一審の専属的合意管轄裁判所とします。</p>
        <p class="subtitle">第19条（準拠法）</p>
        <p>本利用規約は、日本国の法律に基づき解釈されます。</p>
        <BR/>
        <p>附則</p>
        <BR/>
        <p>2020年8月12日　制定</p>
        <DIV>2021年3月17日　改定</DIV>
        <DIV>2021年9月2日　改定</DIV>
        <DIV>2021年11月17日　改定</DIV>
        <DIV>2022年4月1日　改定</DIV>
        <DIV>2022年5月17日　改定</DIV>
        <DIV>2022年6月1日　改定</DIV>
        <DIV>2022年8月9日　改定</DIV>
        <DIV>2024年2月27日　改定</DIV>
        <BR/>
    </div>
  </div>
</template>
